import { useTranslation } from 'react-i18next';
import { FieldError, get } from 'react-hook-form';
import { RadioGroup } from '@anyui/core';
import { FormAction } from '@anytag/shared-types';
import {
  Form,
  Radio,
  ModalBody,
  BaseModal,
  ModalFooter,
  ButtonProps,
  ErrorMessage,
  BaseModalProps
} from '@anytag/shared-atoms';
import { TextField } from '../../rhf-fields';
import { getCsvPagesAndRowsValidationSchema, CSV_EXPORT_TYPE } from './validation-schema';
import { DownloadCsvPagesAndRowsModalKeys, DownloadCsvPagesAndRowsModalValues } from './types';

export interface DownloadCsvPagesAndRowsModalProps
  extends FormAction<DownloadCsvPagesAndRowsModalValues>,
    Pick<BaseModalProps, 'open' | 'onClose'> {
  limit?: number;
  maxRows: number;
  title?: React.ReactNode;
  downloading?: ButtonProps['loading'];
}

export const DownloadCsvPagesAndRowsModal = ({
  open,
  limit,
  title,
  maxRows,
  onClose,
  onSubmit,
  downloading
}: DownloadCsvPagesAndRowsModalProps) => {
  const { t } = useTranslation();

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title || t('CSV Export')}
      description={t('downloadCsvPageRowDescription', { count: maxRows })}
      asChild
    >
      <Form.Provider<DownloadCsvPagesAndRowsModalValues>
        onSubmit={onSubmit}
        defaultValues={{ limit, exportType: CSV_EXPORT_TYPE.PAGES }}
        zodSchema={getCsvPagesAndRowsValidationSchema(t, maxRows)}
      >
        {({ values, setValue, formState: { errors } }) => (
          <>
            <ModalBody>
              <div className="flex flex-col gap-6">
                <RadioGroup
                  direction="vertical"
                  value={values.exportType}
                  onChange={(value) => setValue('exportType', value as CSV_EXPORT_TYPE, { shouldValidate: true })}
                >
                  <Radio label={t('General.Pages')} value={CSV_EXPORT_TYPE.PAGES} />
                  {values.exportType === CSV_EXPORT_TYPE.PAGES ? (
                    <div className="flex items-center gap-2">
                      <TextField<DownloadCsvPagesAndRowsModalKeys> name="firstPage" type="number">
                        {values.exportType === CSV_EXPORT_TYPE.PAGES &&
                        !(get(errors, 'firstPage') as FieldError)?.message &&
                        !(get(errors, 'lastPage') as FieldError)?.message ? (
                          <ErrorMessage
                            className="w-max text-black-60"
                            message={t<string>('exportAtMostPagesAmount', {
                              count: Math.round(maxRows / values.limit)
                            })}
                          />
                        ) : null}
                      </TextField>
                      <span>-</span>
                      <TextField<DownloadCsvPagesAndRowsModalKeys> name="lastPage" type="number" />
                      <span>{t('General.Pages').toLowerCase()}</span>
                    </div>
                  ) : null}

                  <Radio label={t('General.Rows')} value={CSV_EXPORT_TYPE.ROWS} />
                  {values.exportType === CSV_EXPORT_TYPE.ROWS ? (
                    <div className="flex items-center gap-2">
                      <TextField<DownloadCsvPagesAndRowsModalKeys> name="firstRow" type="number">
                        {values.exportType === CSV_EXPORT_TYPE.ROWS &&
                        !(get(errors, 'firstRow') as FieldError)?.message &&
                        !(get(errors, 'lastRow') as FieldError)?.message ? (
                          <ErrorMessage
                            className="w-max text-black-60"
                            message={t<string>('exportAtMostRowsAmount', { count: maxRows })}
                          />
                        ) : null}
                      </TextField>
                      <span>-</span>
                      <TextField<DownloadCsvPagesAndRowsModalKeys> name="lastRow" type="number" />
                      <span>{t('General.Rows').toLowerCase()}</span>
                    </div>
                  ) : null}
                </RadioGroup>
              </div>
            </ModalBody>

            <ModalFooter
              cancelButton={{ onClick: onClose }}
              submitButton={{ loading: downloading, title: t('Button.Export'), type: 'submit' }}
            />
          </>
        )}
      </Form.Provider>
    </BaseModal>
  );
};

export * from './types';
